import apiClient from "@/services/api-client";
import type {
  IntegrationAdminStatus,
  ProviderStatus,
  ScopeInfo,
  IntegrationScopesResponse,
} from "@/generated";

export interface CacheStats {
  total_keys: number;
}

export interface CacheDeletePatternResponse {
  deleted_count: number;
}

export interface GoToLinkResponse {
  resource_type: "workflow" | "kb" | "agent";
  resource_id: string;
  resource_name: string;
  group_id: string;
  group_name: string;
  tenant_id: string;
  tenant_name: string;
}

export interface BillingRectificationModelSummary {
  model: string;
  event_count: number;
  duplicate_events: number;
  sentinel_events: number;
  correctable_events: number;
  uncorrectable_events: number;
  old_cost: number;
  new_cost: number;
  delta: number;
}

export interface BillingRectificationSummary {
  total_events: number;
  duplicate_events: number;
  sentinel_events: number;
  correctable_events: number;
  uncorrectable_events: number;
  by_model: BillingRectificationModelSummary[];
  total_old_cost: number;
  total_new_cost: number;
  total_delta: number;
}

export interface BillingRectificationEvent {
  event_id: string;
  model: string;
  provider: string | null;
  tenant_id: string;
  timestamp: string;
  prompt_tokens: number;
  completion_tokens: number;
  cache_read_tokens: number;
  old_total_cost: number;
  old_input_cost: number;
  old_output_cost: number;
  new_total_cost: number | null;
  new_input_cost: number | null;
  new_output_cost: number | null;
  delta: number | null;
  status:
    | "corrected"
    | "model_not_found"
    | "still_unresolvable"
    | "no_change"
    | "missing_data";
}

export interface BillingRectificationEventsResponse {
  items: BillingRectificationEvent[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface BillingRectificationApplyResult {
  applied: number;
  skipped: number;
  failed: number;
}

export const AdminService = {
  getUsers: async (search?: string) => {
    return await apiClient.get<
      { display_name: string; email: string; id: string }[]
    >(`/api/backend/admin/users/spot${search ? `?search=${search}` : ""}`);
  },
  toggleAdmin: ({ id, is_admin }: { id: string; is_admin: boolean }) => {
    return new Promise((resolve, reject) => {
      apiClient
        .post("/api/backend/admin/users/" + id + "/set-admin", {
          is_admin: is_admin,
        })
        .then(resolve)
        .catch(reject);
    });
  },
  refreshUsers: () => {
    return new Promise((resolve, reject) => {
      apiClient
        .post("/api/backend/admin/users/auth0/refresh")
        .then(resolve)
        .catch(reject);
    });
  },
  authorize: ({ user_id }: { user_id: string }) => {
    return new Promise((resolve, reject) => {
      apiClient
        .post("/api/backend/admin/users/spot/authorize", {
          user_id,
        })
        .then(resolve)
        .catch(reject);
    });
  },
  unauthorize: ({ user_id }: { user_id: string }) => {
    return new Promise((resolve, reject) => {
      apiClient
        .post("/api/backend/admin/users/spot/unauthorize", {
          user_id,
        })
        .then(resolve)
        .catch(reject);
    });
  },
  getRecoveryStats: async (
    lookbackQueued?: number,
    lookbackRecent?: number
  ) => {
    return await apiClient.get<{
      queued_count: number;
      running_count: number;
      recent_running_count: number;
    }>(
      `/api/backend/admin/runs/recovery-stats?lookback_queued=${lookbackQueued || ""}&lookback_recent=${lookbackRecent || ""}`
    );
  },
  resumeRecent: async (lookback?: number) => {
    return await apiClient.post(
      `/api/backend/admin/runs/resume-recent${lookback ? `?lookback=${lookback}` : ""}`
    );
  },
  resumeQueued: async (lookback?: number) => {
    return await apiClient.post(
      `/api/backend/admin/runs/resume-queued${lookback ? `?lookback=${lookback}` : ""}`
    );
  },
  resumeRun: async (runId: string) => {
    return await apiClient.post(`/api/backend/admin/runs/${runId}/resume`);
  },
  stopRun: async (runId: string) => {
    return await apiClient.post(`/api/backend/admin/runs/${runId}/stop`);
  },

  // --- Integration / Provider admin ---

  listIntegrations: async () => {
    return await apiClient.get<IntegrationAdminStatus[]>(
      `/api/backend/admin/integrations`
    );
  },

  getProviderConfigSchema: async (
    integrationTypeHint: string,
    providerKey: string
  ) => {
    return await apiClient.get<ProviderConfigSchemaResponse>(
      `/api/backend/admin/integrations/${integrationTypeHint}/providers/${providerKey}/config`
    );
  },

  saveProviderConfig: async (
    integrationTypeHint: string,
    providerKey: string,
    config: Record<string, string>,
    enabled: boolean = true
  ) => {
    return await apiClient.put(
      `/api/backend/admin/integrations/${integrationTypeHint}/providers/${providerKey}`,
      { config, enabled }
    );
  },

  setIntegrationEnabled: async (integrationType: string, enabled: boolean) => {
    return await apiClient.put(
      `/api/backend/admin/integrations/${integrationType}/enabled`,
      { enabled }
    );
  },

  setIntegrationScopes: async (
    integrationType: string,
    activeKeys: string[] | null
  ) => {
    return await apiClient.put(
      `/api/backend/admin/integrations/${integrationType}/scopes`,
      { active_scope_keys: activeKeys }
    );
  },

  updateIntegrationSettings: async (
    integrationType: string,
    settings: {
      enabled?: boolean | null;
      disabled_nodes?: string[] | null;
      disabled_triggers?: string[] | null;
    }
  ) => {
    return await apiClient.put(
      `/api/backend/admin/integrations/${integrationType}/settings`,
      settings
    );
  },

  deleteProviderConfig: async (
    integrationTypeHint: string,
    providerKey: string
  ) => {
    return await apiClient.delete(
      `/api/backend/admin/integrations/${integrationTypeHint}/providers/${providerKey}`
    );
  },

  setProviderEnabled: async (
    integrationTypeHint: string,
    providerKey: string,
    enabled: boolean
  ) => {
    // Saves an empty config row just to record the enabled/disabled state.
    // Used for NCS-managed providers where there are no credentials to configure.
    return await apiClient.put(
      `/api/backend/admin/integrations/${integrationTypeHint}/providers/${providerKey}`,
      { config: {}, enabled }
    );
  },

  // Cache management
  getCacheStats: async () => {
    return await apiClient.get<CacheStats>(`/api/backend/admin/cache/stats`);
  },
  invalidateCacheTag: async (tag: string) => {
    return await apiClient.post(`/api/backend/admin/cache/invalidate-tag`, {
      tag,
    });
  },
  invalidateAllCache: async () => {
    return await apiClient.post(`/api/backend/cache/invalidate`);
  },
  deleteCachePattern: async (pattern: string) => {
    return await apiClient.post<CacheDeletePatternResponse>(
      `/api/backend/admin/cache/delete-pattern`,
      { pattern }
    );
  },

  // Encrypt plaintext provider keys
  encryptProviderKeys: async () => {
    return await apiClient.post<{
      success: boolean;
      encrypted: number;
      failed: number;
      message: string;
    }>(`/api/backend/admin/providers/encrypt`);
  },

  // Legacy credential migration (secret-auth + Nango OAuth)
  migrateLegacyCredentials: async () => {
    return await apiClient.post<{
      success: boolean;
      secret_auth_migrated: number;
      secret_auth_failed: number;
      oauth_migrated: number;
      oauth_skipped: number;
      oauth_failed: number;
      message: string;
    }>(`/api/backend/admin/credentials/migrate-legacy`);
  },

  // Upgrade OAuth credentials to NCS where the provider is now NCS-managed
  upgradeToNCS: async () => {
    return await apiClient.post<{
      success: boolean;
      upgraded: number;
      skipped: number;
      failed: number;
      message: string;
    }>(`/api/backend/admin/credentials/upgrade-to-ncs`);
  },

  // Re-migrate secret-auth credentials (overwrites existing with fresh payload)
  remigrateSecretAuth: async () => {
    return await apiClient.post<{
      success: boolean;
      updated: number;
      skipped: number;
      failed: number;
      message: string;
    }>(`/api/backend/admin/credentials/remigrate-secret-auth`);
  },

  // Migrate legacy Google credentials (split single "google" into per-service)
  migrateGoogleCredentials: async () => {
    return await apiClient.post<{
      success: boolean;
      created: number;
      skipped: number;
      failed: number;
      message: string;
    }>(`/api/backend/admin/credentials/migrate-google`);
  },

  // Go to link
  goToLink: async (url: string) => {
    return await apiClient.post<GoToLinkResponse>(
      `/api/backend/admin/go-to-link`,
      { url }
    );
  },

  // Billing rectification
  getBillingRectificationSummary: async (params: {
    model?: string;
    tenant_id?: string;
    date_from?: string;
    date_to?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params.model) qs.set("model", params.model);
    if (params.tenant_id) qs.set("tenant_id", params.tenant_id);
    if (params.date_from) qs.set("date_from", params.date_from);
    if (params.date_to) qs.set("date_to", params.date_to);
    return await apiClient.get<BillingRectificationSummary>(
      `/api/backend/admin/billing-rectification/summary?${qs.toString()}`
    );
  },

  getBillingRectificationEvents: async (params: {
    model?: string;
    tenant_id?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    size?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params.model) qs.set("model", params.model);
    if (params.tenant_id) qs.set("tenant_id", params.tenant_id);
    if (params.date_from) qs.set("date_from", params.date_from);
    if (params.date_to) qs.set("date_to", params.date_to);
    if (params.page) qs.set("page", String(params.page));
    if (params.size) qs.set("size", String(params.size));
    return await apiClient.get<BillingRectificationEventsResponse>(
      `/api/backend/admin/billing-rectification/events?${qs.toString()}`
    );
  },

  deduplicateBilling: async (body: {
    model?: string;
    tenant_id?: string;
    date_from?: string;
    date_to?: string;
  }) => {
    return await apiClient.post<BillingRectificationApplyResult>(
      `/api/backend/admin/billing-rectification/deduplicate`,
      body
    );
  },

  backfillSentinels: async (body: {
    model?: string;
    tenant_id?: string;
    date_from?: string;
    date_to?: string;
  }) => {
    return await apiClient.post<BillingRectificationApplyResult>(
      `/api/backend/admin/billing-rectification/backfill-sentinels`,
      body
    );
  },

  correctPrices: async (body: {
    model?: string;
    tenant_id?: string;
    date_from?: string;
    date_to?: string;
  }) => {
    return await apiClient.post<BillingRectificationApplyResult>(
      `/api/backend/admin/billing-rectification/correct-prices`,
      body
    );
  },

  runLifecycleHealthChecks: async () => {
    return await apiClient.post<{
      job_id: number;
      message: string;
    }>(`/api/backend/admin/lifecycle/run-health-checks`);
  },

  repairNoxusLinks: async () => {
    return await apiClient.post<{
      links_reactivated: number;
      tenants_affected: number;
      health_check_job_id: number | null;
    }>(`/api/backend/admin/noxus-provider/repair`);
  },
};

// --- Types for integration admin (re-exported from generated spec) ---

export type {
  IntegrationAdminStatus,
  IntegrationScopesResponse,
} from "@/generated";

// Aliases matching the names used in the admin page components
// Extends ProviderStatus with configured_at (not yet in generated types)
export type ProviderAdminStatus = ProviderStatus & {
  configured_at?: string | null;
};
export type ScopeAdminInfo = ScopeInfo;

export interface ProviderConfigField {
  type: string;
  description: string | null;
  optional: boolean;
  default: string | null;
  display: {
    type: string;
    label: string;
    placeholder?: string;
    tooltip?: string;
    help_text?: string;
  } | null;
}

export interface ProviderConfigSchemaResponse {
  config: Record<string, ProviderConfigField>;
  inputs: [string, string][];
  ready: boolean;
  title: string;
}
